import { BRIEFING_ANCHOR, briefingPath, modulePath } from "@colossus/contracts";
import {
  PRODUCT_BACKFILL_READ,
  PRODUCT_BACKFILL_WRITE,
  TEAM_ACCESS_READ,
  TEAM_ACCESS_WRITE,
  TEAM_AUDIT_READ,
  TEAM_CREDENTIALS_WRITE,
  TEAM_BOT_WRITE,
  TEAM_SETTINGS_DATA_PURGE,
  TEAM_LIFECYCLE_DELETE,
  TEAM_SETTINGS_DATA_READ,
  TEAM_SETTINGS_DATA_WRITE,
  TEAM_SETTINGS_SERVER_READ,
  TEAM_SETTINGS_SERVER_WRITE,
  hasAny,
} from "./access.ts";
import { BILLING_KEYS } from "@colossus/contracts/first-reads";
import {
  AUDIT_ACTIONS,
  LEVELING_CONFIG_WRITE,
  LEVELING_IMPORTS_READ,
  LEVELING_IMPORTS_WRITE,
  isDiscordUserId,
} from "@colossus/contracts";
import type { AuditAction } from "@colossus/contracts";
import type { ApiFailure, ConsoleCode } from "@colossus/api-client";
import { awaitingCheckout } from "./dashboard.ts";
import { levelingHref, levelingShown } from "./leveling-nav.ts";
import { m } from "./paraglide/messages.js";
import { refusalOf } from "./refusal.ts";
import type { RefusalTable } from "./refusal.ts";
import type { Message } from "./locale.ts";

export const ACTIVITY_PAGE_SIZE = 20;

export { BILLING_KEYS };

export const SERVER_KEYS: readonly string[] = [
  TEAM_SETTINGS_SERVER_READ,
  TEAM_SETTINGS_SERVER_WRITE,
  TEAM_BOT_WRITE,
];
export const DATA_KEYS: readonly string[] = [
  TEAM_SETTINGS_DATA_READ,
  TEAM_SETTINGS_DATA_WRITE,
  TEAM_SETTINGS_DATA_PURGE,
];
export const DANGER_KEYS: readonly string[] = [TEAM_CREDENTIALS_WRITE, TEAM_LIFECYCLE_DELETE];
const MODULE_KEYS: readonly string[] = [LEVELING_CONFIG_WRITE];

export const ACCESS_KEYS: readonly string[] = [TEAM_ACCESS_READ, TEAM_ACCESS_WRITE];
export const AUDIT_KEYS: readonly string[] = [TEAM_AUDIT_READ];

const SECTIONS = [
  {
    id: "general",
    title: m.settings_section_general,
    needs: [...SERVER_KEYS, ...DATA_KEYS, ...MODULE_KEYS, ...DANGER_KEYS],
  },
  { id: "billing", title: m.billing_title, needs: BILLING_KEYS },
  {
    id: "access",
    title: m.settings_section_team,
    needs: [...ACCESS_KEYS, ...AUDIT_KEYS],
  },
] as const satisfies readonly {
  readonly id: string;
  readonly title: Message;
  readonly needs: readonly string[];
}[];

export type SectionId = (typeof SECTIONS)[number]["id"];

export type Section = (typeof SECTIONS)[number];

export const visibleSections = (permissions: readonly string[]): readonly Section[] =>
  SECTIONS.filter((section) => hasAny(permissions, section.needs));

const SECTION_SLUG: Readonly<Record<SectionId, string | null>> = {
  general: null,
  billing: "billing",
  access: "team",
};

const settingsRoot = (tenantId: string): string => modulePath(tenantId, "workspace");

export const settingsHref = (tenantId: string, section: SectionId): string => {
  const slug = SECTION_SLUG[section];
  return slug === null ? settingsRoot(tenantId) : `${settingsRoot(tenantId)}/${slug}`;
};

export const DANGER_ANCHOR = "danger";

export { BRIEFING_ANCHOR };

export const briefingHref = briefingPath;

const HISTORY_IMPORT_KEYS: readonly string[] = [PRODUCT_BACKFILL_READ, PRODUCT_BACKFILL_WRITE];

export const HISTORY_ANCHOR = "history";

interface ImportReader {
  readonly grants: readonly string[];
  readonly permissions: readonly string[];
}

export const historyImportShown = (permissions: readonly string[]): boolean =>
  hasAny(permissions, HISTORY_IMPORT_KEYS);

export const levelsImportShown = (reader: ImportReader): boolean =>
  levelingShown(reader.grants) &&
  hasAny(reader.permissions, [LEVELING_IMPORTS_READ, LEVELING_IMPORTS_WRITE]);

export const importHref = (tenantId: string): string => modulePath(tenantId, "workspace", "import");

export const historyImportHref = (tenantId: string): string =>
  `${importHref(tenantId)}#${HISTORY_ANCHOR}`;

export const RECORDING_ANCHOR = "recording";

export const recordingHref = (tenantId: string): string =>
  `${settingsRoot(tenantId)}#${RECORDING_ANCHOR}`;

export const ACTIVITY_ANCHOR = "activity";

const FOLDED: Readonly<Record<string, string>> = {
  server: "",
  data: `#${RECORDING_ANCHOR}`,
  danger: `#${DANGER_ANCHOR}`,
  activity: `/${SECTION_SLUG.access}#${ACTIVITY_ANCHOR}`,
};

export const foldedSection = (tenantId: string, slug: string | undefined): string | null => {
  if (slug === undefined) return null;
  if (slug === "modules") return levelingHref(tenantId, "earning/rules");
  const anchor = FOLDED[slug];
  return anchor === undefined ? null : `${settingsRoot(tenantId)}${anchor}`;
};

export const sectionAt = (slug: string | undefined): SectionId | null | undefined => {
  if (slug === undefined) return null;
  const found = SECTIONS.find((section) => SECTION_SLUG[section.id] === slug);
  return found === undefined ? undefined : found.id;
};

export const keepingQuery = (target: string, from: { readonly search: string }): string => {
  const [path = target, anchor] = target.split("#");
  return `${path}${from.search}${anchor === undefined ? "" : `#${anchor}`}`;
};

export const movedSection = (tenantId: string, hash: string): string | null => {
  const anchor = hash.replace(/^#/, "");
  if (anchor === BRIEFING_ANCHOR) return briefingHref(tenantId);
  if (anchor === "plans" || anchor === "payment")
    return `${settingsHref(tenantId, "billing")}#${anchor}`;
  if (anchor === "leveling") return levelingHref(tenantId, "earning/rules");
  const section = SECTIONS.find((entry) => entry.id === anchor);
  return section === undefined
    ? foldedSection(tenantId, anchor)
    : settingsHref(tenantId, section.id);
};

type BillingAnchor = "billing" | "plans" | "payment";

export const billingHref = (
  tenantId: string,
  anchor: BillingAnchor = "billing",
  query: URLSearchParams | null = null,
): string =>
  `${settingsHref(tenantId, "billing")}${query === null ? "" : `?${query.toString()}`}${
    anchor === "billing" ? "" : `#${anchor}`
  }`;

export const atAnchor = (href: string, anchor: BillingAnchor): string => {
  const base = href.split("#")[0] ?? href;
  return anchor === "billing" ? base : `${base}#${anchor}`;
};

export const checkoutGate = (
  tenant: {
    readonly tenantId: string;
    readonly sharedBot: boolean;
    readonly botAppId: string | null;
    readonly status: string;
    readonly grants: readonly string[];
    readonly permissions: readonly string[];
    readonly awaitingSharedBot?: boolean;
  },
  pathname: string,
): string | null => {
  if (!awaitingCheckout(tenant) || !hasAny(tenant.permissions, BILLING_KEYS)) return null;
  return pathname === settingsHref(tenant.tenantId, "billing")
    ? null
    : billingHref(tenant.tenantId);
};

type OwnBotCode = ConsoleCode<"post", "/api/tenants/{tenantId}/custom-bot">;

const OWN_BOT_REFUSALS: RefusalTable<OwnBotCode> = {
  bot_not_installed: m.settings_own_bot_not_installed,
  bot_enrolled: m.settings_own_bot_enrolled,
  bot_mismatch: m.settings_own_bot_token_rejected,
  custody_refused: m.settings_own_bot_token_rejected,
  token_invalid: m.settings_own_bot_token_rejected,
  token_malformed: m.settings_own_bot_token_malformed,
  custom_instance_required: m.settings_own_bot_plan_required,
};

type TokenCode = ConsoleCode<"post", "/api/tenants/{tenantId}/token">;

const TOKEN_REFUSALS: RefusalTable<TokenCode> = {
  token_malformed: m.settings_own_bot_token_malformed,
  shared_bot_token: m.tenant_danger_token_shared,
  bot_mismatch: m.tenant_danger_token_mismatch,
  custody_refused: m.settings_own_bot_token_rejected,
  token_invalid: m.settings_own_bot_token_rejected,
};

export const tokenRefusal = (failure: ApiFailure<TokenCode>): string =>
  refusalOf(TOKEN_REFUSALS, failure, m.tenant_settings_refused);

type OwnerCandidate =
  | { readonly ok: true; readonly id: string }
  | { readonly ok: false; readonly reason: Message | null };

export const ownerCandidate = (typed: string, self: string | null): OwnerCandidate => {
  const id = typed.trim();
  if (id === "") return { ok: false, reason: null };
  if (!isDiscordUserId(id)) return { ok: false, reason: m.tenant_owner_invalid };
  if (id === self) return { ok: false, reason: m.tenant_owner_self };
  return { ok: true, id };
};

type TransferCode = ConsoleCode<"post", "/api/tenants/{tenantId}/access/owner">;

const TRANSFER_REFUSALS: RefusalTable<TransferCode> = {
  not_found: m.tenant_owner_not_found,
  already_owner: m.tenant_owner_self,
  transfer_while_paying: m.tenant_owner_while_paying,
};

export const transferRefusal = (failure: ApiFailure<TransferCode>): string =>
  refusalOf(TRANSFER_REFUSALS, failure, m.tenant_settings_refused);

export const ownBotRefusal = (failure: ApiFailure<OwnBotCode>): string =>
  refusalOf(OWN_BOT_REFUSALS, failure, m.settings_own_bot_failed);

const ACTIVITY_LABEL: Readonly<Record<AuditAction, Message>> = {
  "account.suspend": m.activity_account_suspend,
  "account.unsuspend": m.activity_account_unsuspend,
  "account.staff.set": m.activity_account_staff,
  "policy.set": m.activity_policy_set_any,
  "policy.delete": m.activity_policy_delete_any,
  "owner.transfer": m.activity_owner_transfer_any,
  "entitlement.catalog.set": m.activity_entitlement_catalog,
  "entitlement.set": m.activity_entitlement_set,
  "entitlement.delete": m.activity_entitlement_delete,
  "billing.checkout": m.activity_billing_checkout,
  "billing.entitlement.sync": m.activity_billing_sync,
  "billing.revise": m.activity_billing_revise,
  "billing.resume": m.activity_billing_resume,
  "billing.band.drift": m.activity_billing_band,
  "billing.refund.revoked": m.activity_billing_refund,
  "billing.pending.discarded": m.activity_billing_pending,
  "billing.subscription.duplicate": m.activity_billing_duplicate,
  "billing.payment_method.session": m.activity_card_session,
  "billing.payment_method.default": m.activity_card_default,
  "billing.payment_method.delete": m.activity_card_delete,
  "connection.public.register": m.activity_public_register,
  "connection.public.adopt": m.activity_public_adopt,
  "connection.public.fallback": m.activity_public_fallback,
  "connection.custom.adopt": m.activity_custom_adopt,
  "tenant.showcase.set": m.activity_showcase_set,
  "tenant.showcase.ban": m.activity_showcase_ban,
  "tenant.showcase.unban": m.activity_showcase_unban,
  "tenant.reprovision": m.activity_reprovision,
  "tenant.capture.verify": m.activity_capture_verify,
  "tenant.deprovision": m.activity_deprovision,
  "tenant.rebind": m.activity_rebind,
};

const isAuditAction = (action: string): action is AuditAction =>
  (AUDIT_ACTIONS as readonly string[]).includes(action);

export const activityLabel = (action: string): string =>
  isAuditAction(action) ? ACTIVITY_LABEL[action]() : action;
